import { useState } from "react";
import { Button, ButtonVariant, Text } from "@merge-api/merge-javascript-shared";
import { ChevronRight, Plus } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { PlaybookSummary, PlanSummary } from "./types";
import { ACCENT } from "./constants";
import { useConductorResource } from "./hooks/useConductorResource";
import ConductorPlaybooksTable from "./components/ConductorPlaybooksTable";
import ConductorPlansTable from "./components/ConductorPlansTable";
import ConductorPlaybookDetail from "./components/ConductorPlaybookDetail";
import ConductorPlanDetail from "./components/ConductorPlanDetail";
import PlanEditorDrawer from "./components/PlanEditorDrawer";
import PlaybookEditorDrawer from "./components/PlaybookEditorDrawer";
import {
  ConductorRoute,
  ConductorTab,
  createConductorSearch,
  parseConductorRoute,
} from "./conductorRoute";

const SUBTABS = ["Playbooks", "Plans"] as const;

type RouteEntry =
  | { view: "index" }
  | { view: "playbook"; id: string }
  | { view: "plan"; id: string };

const crumbKey = (entry: RouteEntry): string =>
  entry.view === "index" ? "index" : `${entry.view}-${entry.id}`;

// The Conductor tab on an integration's admin page. Its sub-tab and selected
// resources live in the query string, allowing detail pages to be linked and
// reloaded without losing their place.
const ConductorIntegrationTab = ({
  integrationID,
  integrationCategories,
}: {
  integrationID: string;
  integrationCategories: string[];
}) => {
  const history = useHistory();
  const location = useLocation();
  const route = parseConductorRoute(location.search);
  const subtab = route.tab === "plans" ? 1 : 0;
  const stack: RouteEntry[] = [
    { view: "index" },
    ...(route.playbookId ? [{ view: "playbook" as const, id: route.playbookId }] : []),
    ...(route.planId ? [{ view: "plan" as const, id: route.planId }] : []),
  ];
  const [plansReloadToken, setPlansReloadToken] = useState(0);
  const [playbooksReloadToken, setPlaybooksReloadToken] = useState(0);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [creatingPlaybook, setCreatingPlaybook] = useState(false);

  const { data: playbooksData, isLoading: playbooksLoading } = useConductorResource<
    PlaybookSummary[]
  >(
    `/conductor/playbook-summaries?integration_id=${integrationID}`,
    "Error fetching playbooks",
    playbooksReloadToken
  );
  const { data: plansData, isLoading: plansLoading } = useConductorResource<PlanSummary[]>(
    `/conductor/plan-summaries?integration_id=${integrationID}`,
    "Error fetching plans",
    plansReloadToken
  );
  const playbooks = playbooksData || [];
  const plans = plansData || [];
  const categories = integrationCategories;

  const current = stack[stack.length - 1];

  const navigate = (nextRoute: ConductorRoute) => {
    const search = createConductorSearch(location.search, nextRoute);
    if (search !== location.search) {
      history.push({ pathname: location.pathname, search, hash: location.hash });
    }
  };
  const openPlaybook = (id: string) => navigate({ tab: "playbooks", playbookId: id });
  const openPlan = (id: string) =>
    navigate({
      tab: route.playbookId ? "playbooks" : "plans",
      ...(route.playbookId ? { playbookId: route.playbookId } : {}),
      planId: id,
    });
  const goToIndex = (tab: ConductorTab) => navigate({ tab });
  const goBack = () => {
    if (route.planId && route.playbookId) {
      navigate({ tab: "playbooks", playbookId: route.playbookId });
    } else {
      goToIndex(route.tab);
    }
  };
  const goToCrumb = (index: number) => {
    const entry = stack[index];
    if (!entry || entry.view === "index") {
      goToIndex(route.tab);
    } else if (entry.view === "playbook") {
      navigate({ tab: "playbooks", playbookId: entry.id });
    } else {
      navigate({
        tab: route.playbookId ? "playbooks" : "plans",
        ...(route.playbookId ? { playbookId: route.playbookId } : {}),
        planId: entry.id,
      });
    }
  };

  const crumbLabel = (entry: RouteEntry): string => {
    if (entry.view === "index") return subtab === 0 ? "Playbooks" : "Plans";
    if (entry.view === "playbook") {
      const pb = playbooks.find((p) => p.id === entry.id);
      if (!pb) return "Playbook";
      if (pb.organization_id) return `${pb.organization_name || "Org"} playbook`;
      if (pb.linked_account_id) return "LA playbook";
      return "Default playbook";
    }
    return plans.find((p) => p.id === entry.id)?.name || "Plan";
  };

  return (
    <div className="pb-8">
      {stack.length > 1 && (
        <div className="flex items-center gap-2 mb-6">
          {stack.map((entry, index) => {
            const isLast = index === stack.length - 1;
            return (
              <span key={crumbKey(entry)} className="flex items-center gap-2">
                {index > 0 && <ChevronRight size={14} className="text-gray-40" />}
                {isLast ? (
                  <Text variant="sm" className="text-gray-90 font-semibold">
                    {crumbLabel(entry)}
                  </Text>
                ) : (
                  <button onClick={() => goToCrumb(index)} className="hover:underline">
                    <Text variant="sm" className="text-gray-60">
                      {crumbLabel(entry)}
                    </Text>
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {current.view === "index" && (
        <>
          {/* Sub-tabs of the Conductor page: smaller than the integration-page
              tabs, with a thin orange underline on the active one. */}
          <div className="flex gap-6 border-b border-gray-10 mb-8">
            {SUBTABS.map((label, index) => (
              <button
                key={label}
                onClick={() => goToIndex(index === 0 ? "playbooks" : "plans")}
                className={`-mb-px border-b-2 px-1 pb-2 text-sm font-semibold transition ${
                  subtab === index
                    ? "text-gray-90"
                    : "border-b-transparent text-gray-60 hover:text-gray-90"
                }`}
                style={subtab === index ? { borderBottomColor: ACCENT } : undefined}
              >
                {label}
              </button>
            ))}
          </div>

          {subtab === 0 ? (
            <>
              <div className="mb-6 flex justify-end">
                <Button
                  size="sm"
                  variant={ButtonVariant.PrimaryGray}
                  leftIcon={<Plus size={14} />}
                  onClick={() => setCreatingPlaybook(true)}
                >
                  New playbook
                </Button>
              </div>
              <ConductorPlaybooksTable
                playbooks={playbooks}
                isLoading={playbooksLoading}
                onOpen={openPlaybook}
              />
            </>
          ) : (
            <>
              <div className="mb-6 flex justify-end">
                <Button
                  size="sm"
                  variant={ButtonVariant.PrimaryGray}
                  leftIcon={<Plus size={14} />}
                  onClick={() => setCreatingPlan(true)}
                >
                  New plan
                </Button>
              </div>
              <ConductorPlansTable plans={plans} isLoading={plansLoading} onOpen={openPlan} />
            </>
          )}
        </>
      )}

      {current.view === "playbook" && (
        <ConductorPlaybookDetail
          playbookId={current.id}
          onOpenPlan={openPlan}
          onDeleted={() => {
            goBack();
            setPlaybooksReloadToken((token) => token + 1);
          }}
        />
      )}

      {current.view === "plan" && (
        <ConductorPlanDetail
          planId={current.id}
          onOpenPlan={openPlan}
          onDeleted={() => {
            goBack();
            setPlansReloadToken((token) => token + 1);
          }}
        />
      )}

      {creatingPlan && (
        <PlanEditorDrawer
          integrationId={integrationID}
          categories={categories}
          onClose={() => setCreatingPlan(false)}
          onCreated={(planId) => {
            setCreatingPlan(false);
            setPlansReloadToken((token) => token + 1);
            if (planId) openPlan(planId);
          }}
        />
      )}

      {creatingPlaybook && (
        <PlaybookEditorDrawer
          integrationId={integrationID}
          categories={categories}
          onClose={() => setCreatingPlaybook(false)}
          onCreated={(playbookId) => {
            setCreatingPlaybook(false);
            setPlaybooksReloadToken((token) => token + 1);
            if (playbookId) openPlaybook(playbookId);
          }}
        />
      )}
    </div>
  );
};

export default ConductorIntegrationTab;
